import { ClaimRequest, PaymentMethod } from '@bybeam/platform-types';
import RadioGroup from '@platform-ui-common/components/RadioGroup/index.js';
import useForm, { requiredField, ValidationMode } from '@platform-ui-common/hooks/useForm/index.js';
import {
  PaymentMethodDisplay,
  hasAuthorizedPayment,
} from '@platform-ui-common/utilities/payment.js';
import { PaymentRail } from '@platform-ui-common/utilities/paymentMethods.js';
import ClaimRoutes from '@platform-ui/claim/ClaimRoutes.js';
import ClaimForm from '@platform-ui/claim/components/ClaimForm/index.js';
import useClaim from '@platform-ui/claim/hooks/useClaim.js';
import { makeRoute, useUserHomeRoute } from '@platform-ui/utilities/Routes.js';
import { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { ZelleNetwork } from '../Zelle/utils.js';

const paymentMethodsContent: Record<
  Exclude<PaymentMethod, PaymentMethod.External>,
  { displayName: string; description: string | JSX.Element }
> = {
  [PaymentMethod.DirectDeposit]: {
    displayName: PaymentMethodDisplay[PaymentMethod.DirectDeposit],
    description:
      'We recommend using this option if you have access to a bank account. You should receive your funds in 1-3 business days.',
  },
  [PaymentMethod.Zelle]: {
    displayName: PaymentMethodDisplay[PaymentMethod.Zelle],
    description: (
      <>
        Receive your funds within minutes by claiming with Zelle. Make sure your bank is in the{' '}
        {<ZelleNetwork />}.
      </>
    ),
  },
  [PaymentMethod.VirtualCard]: {
    displayName: PaymentMethodDisplay[PaymentMethod.VirtualCard],
    description:
      'Receive your funds within minutes after creating an Akimbo Now prepaid card account.',
  },
  [PaymentMethod.PhysicalCard]: {
    displayName: PaymentMethodDisplay[PaymentMethod.PhysicalCard],
    description: 'Receive a physical prepaid card in the mail within 7 business days.',
  },
  [PaymentMethod.Check]: {
    displayName: PaymentMethodDisplay[PaymentMethod.Check],
    description: 'Receive a check in the mail in 7-10 business days.',
  },
};

function PaymentMethodSelection(): JSX.Element {
  const history = useHistory();
  const userHomeRoute = useUserHomeRoute();
  const { method, setMethod, paymentRails, fulfillment } = useClaim<ClaimRequest>();
  useEffect(() => {
    if (fulfillment && !hasAuthorizedPayment(fulfillment)) history.push(userHomeRoute);
  }, [history, userHomeRoute, fulfillment]);

  const { formData, dispatch, errors, trySubmit } = useForm(
    { method: (method ?? '') as PaymentRail },
    { method: requiredField() },
    ValidationMode.RequiredOnSubmit,
  );

  const errorMessages = Object.values(errors).flat();

  const onSubmit = (): void =>
    trySubmit(() => {
      setMethod(formData.method);
      history.push(makeRoute(ClaimRoutes.PRIMER, { fulfillmentId: fulfillment.id }));
    });

  const items = useMemo(
    () =>
      Object.entries(paymentMethodsContent)
        .filter(([key]) => paymentRails.includes(key as PaymentMethod))
        .map(([key, { displayName, description }]) => ({
          label: displayName,
          description,
          value: key,
        })),
    [paymentRails],
  );

  return (
    <ClaimForm
      header="How would you like to receive your funds?"
      subHeader="Please select one option:"
      onSubmit={onSubmit}
      errors={errorMessages}
    >
      <div className="w-full max-w-[320px]">
        <RadioGroup
          items={items}
          selectedValue={formData.method}
          onChange={(value: PaymentRail): void => {
            dispatch({
              type: 'simpleUpdate',
              payload: { field: 'method', value },
            });
          }}
          error={!!errors?.method}
          name="method"
        />
      </div>
    </ClaimForm>
  );
}

export default PaymentMethodSelection;
